import React, { useState, useEffect, useMemo } from "react";
import ApptHeader from "./ApptHeader";
import MiniCalendar from "./MiniCalendar";
import NoAppointment from "./NoAppointment";
import appointmentService from "../../../services/appointment.service";
import clinicService from "../../../services/clinic.service";
import Loading from "../../common/Loading";
import { toast } from "react-toastify";
import { useEyeconxStore } from "../../../store/eyeconxStore";
import AppointmentList from "./AppointmentList";

function Appointments() {
  const user = useEyeconxStore((state) => state.user);

  const [loading, setLoading] = useState(false);
  const [appointmentDate, setAppointmentDate] = useState(new Date());

  const [totalAppointment, setTotalAppointment] = useState(0);
  const [confirmedAppointment, setConfirmedAppointment] = useState(0);
  const [apptTitle, setApptTitle] = useState("");

  const [docId, setDocId] = useState(0);
  const [doctorList, setDoctorList] = useState([]);
  const [appointmentList, setAppointmentList] = useState([]);

  const [checkedItems, setCheckedItems] = useState([]); // appointment ids
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const changeDate = (date) => {
    setAppointmentDate(date);
  };

  // Manager: load doctors once
  useEffect(() => {
    if (user.role === "ROLE_MANAGER") {
      clinicService
        .getUserByRole(user.clinicId, "ecp")
        .then((res) => setDoctorList(res.data || []))
        .catch(() => toast.error("Error loading doctor list"));
    }
  }, [user.role, user.clinicId]);

  // Update title + load appointments whenever date / doc changes
  useEffect(() => {
    const today = new Date();
    const selected = new Date(appointmentDate);

    if (
      today.getDate() === selected.getDate() &&
      today.getMonth() === selected.getMonth() &&
      today.getFullYear() === selected.getFullYear()
    ) {
      setApptTitle("Today's Appointments");
    } else {
      setApptTitle("Appointments on " + selected.toDateString());
    }

    loadAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentDate, docId]);

  const loadAppointments = () => {
    setLoading(true);

    const y = appointmentDate.getFullYear();
    const m = String(appointmentDate.getMonth() + 1).padStart(2, "0");
    const d = String(appointmentDate.getDate()).padStart(2, "0");
    const apptdate = `${y}-${m}-${d}`;

    appointmentService
      .getAppointment(user.clinicId, apptdate, docId)
      .then((res) => {
        setLoading(false);
        const list = Array.isArray(res.data) ? res.data : (res.data.appointmentsList || []);


        setAppointmentList(list);
        setTotalAppointment(list.length);
        setConfirmedAppointment(list.filter(appt => appt.confirmed).length);
        setCheckedItems([]); // reset selections on reload
      })
      .catch(() => {
        setLoading(false);
        toast.error("Error while getting appointments.");
      });
  };

  // Quick date buttons
  const goToToday = () => setAppointmentDate(new Date());
  const goToPreviousDay = () =>
    setAppointmentDate((prev) => new Date(prev.getTime() - 24 * 60 * 60 * 1000));
  const goToNextDay = () =>
    setAppointmentDate((prev) => new Date(prev.getTime() + 24 * 60 * 60 * 1000));

  const handleDocChange = (e) => {
    const value = Number(e.target.value);
    setDocId(value);
  };

  // Unique appointment types for filter
  const appointmentTypes = useMemo(() => {
    const set = new Set();
    appointmentList.forEach((appt) => {
      if (appt.type) set.add(appt.type);
    });
    return Array.from(set);
  }, [appointmentList]);

  // Filtered list
  const filteredAppointments = useMemo(() => {
    return appointmentList.filter((appt) => {
      const matchesType =
        typeFilter === "ALL" ? true : appt.type === typeFilter;

      const matchesStatus =
        statusFilter === "ALL"
          ? true
          : String(appt.isConfirmed) === String(statusFilter);

      return matchesType && matchesStatus;
    });
  }, [appointmentList, typeFilter, statusFilter]);

  // Derived all-selected state
  const allSelected =
    filteredAppointments.length > 0 &&
    checkedItems.length === filteredAppointments.length;

  // Single row selection
  const handleItemCheckChange = (appointmentId, isChecked) => {
    setCheckedItems((prev) => {
      if (isChecked) {
        if (prev.includes(appointmentId)) return prev;
        return [...prev, appointmentId];
      } else {
        return prev.filter((id) => id !== appointmentId);
      }
    });
  };

  // Select / deselect all (only filtered list)
  const handleToggleAll = (e) => {
    const checked = e.target.checked;
    if (checked) {
      setCheckedItems(filteredAppointments.map((appt) => appt.id));
    } else {
      setCheckedItems([]);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const confirmedStatuses = [
    { value: "0", label: "Not Confirmed" },
    { value: "1", label: "Confirmed by Eyeconx" },
    { value: "2", label: "Confirmed by PMS" },
    { value: "3", label: "Cancelled" },
  ];

  return (
    <>
      <ApptHeader
        totalAppointment={totalAppointment}
        confirmedAppointment={confirmedAppointment}
      />

      <div className="app-container container-xxl mt-3">
        <div className="row">
          {/* LEFT COLUMN - STICKY MINI CALENDAR + selection */}
          <div className="col-md-3 mb-4">
            <div className="position-sticky" style={{ top: "1rem" }}>
              <MiniCalendar currentDate={appointmentDate} changeDate={changeDate} />

              {checkedItems.length > 0 && (
                <div className="mt-4 p-3 bg-light border rounded">
                  <span className="text-muted fw-bold d-block mb-2">
                    Selected Patients
                  </span>

                  {filteredAppointments
                    .filter((appt) => checkedItems.includes(appt.id))
                    .map((appt) => (
                      <div key={appt.id} className="text-muted small">
                        {appt.patient?.firstName} {appt.patient?.lastName}
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT COLUMN - HEADER + FILTERS + LIST */}
          <div className="col-md-9">
            <div className="card border-0 shadow-sm">
              <div className="card-header border-0 pt-1 pb-3">

              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">

  {/* LEFT TITLE SECTION */}
  <div className="d-flex flex-column">
    <h3 className="card-title mb-0 fw-bold fs-4 text-truncate" style={{ maxWidth: "280px" }}>
      {apptTitle}
    </h3>
    <small className="text-muted fw-semibold">
      Total: {totalAppointment} — Confirmed: {confirmedAppointment}
    </small>
  </div>

  {/* RIGHT FILTER + ACTIONS */}
  <div className="d-flex flex-wrap gap-2 align-items-center">

    {/* Doctor Filter */}
    {user.role === "ROLE_MANAGER" && (
      <select
        className="form-select form-select-sm"
        onChange={handleDocChange}
        style={{
          width: "auto",
          minWidth: "140px",
          maxWidth: "180px",
        }}
      >
        <option value="0">All Doctors</option>
        {doctorList.map((doc) => (
          <option key={doc.id} value={doc.id}>
            {doc.firstName} {doc.lastName}
          </option>
        ))}
      </select>
    )}

    {/* Type Filter */}
    <select
      className="form-select form-select-sm"
      value={typeFilter}
      onChange={(e) => setTypeFilter(e.target.value)}
      style={{
        width: "auto",
        minWidth: "120px",
        maxWidth: "150px",
      }}
    >
      <option value="ALL">All Types</option>
      {appointmentTypes.map((t) => (
        <option key={t} value={t}>{t}</option>
      ))}
    </select>

    {/* Status Filter */}
    <select
      className="form-select form-select-sm"
      value={statusFilter}
      onChange={(e) => setStatusFilter(e.target.value)}
      style={{
        width: "auto",
        minWidth: "120px",
        maxWidth: "150px",
      }}
    >
      <option value="ALL">All Status</option>
      {confirmedStatuses.map((s) => (
        <option key={s.value} value={s.value}>{s.label}</option>
      ))}
    </select>

    {/* Print Button */}
    <button
      className="btn btn-sm btn-outline-secondary d-flex align-items-center"
      onClick={handlePrint}
    >
      <i className="bi bi-printer me-1"></i>
      Print
    </button>

  </div>
</div>


                {/* Bulk actions bar */}
                {checkedItems.length > 0 && (
                  <div className="mt-3 pt-3 border-top d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <span className="text-muted">
                      {checkedItems.length} appointment(s) selected
                    </span>
                    <div className="d-flex flex-wrap gap-2">
                      <button className="btn btn-sm btn-light-primary">
                        <i className="bi bi-phone me-1"></i> SMS
                      </button>
                      <button className="btn btn-sm btn-light-info">
                        <i className="bi bi-envelope-fill me-1"></i> Email
                      </button>
                      <button className="btn btn-sm btn-light-success">
                        <i className="bi bi-clipboard-check me-1"></i> Survey
                      </button>
                      <button className="btn btn-sm btn-light-primary">
                        <i className="bi bi-card-text me-1"></i> Articles
                      </button>
                      <button className="btn btn-sm btn-light-info">
                        <i className="bi bi-eyeglasses me-1"></i> Glass Pickup
                      </button>
                      <button className="btn btn-sm btn-light-success">
                        <i className="bi bi-dash-circle me-1"></i> CL Pickup
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="card-body pt-0">
                {loading ? (
                  <Loading />
                ) : filteredAppointments.length > 0 ? (
                  <AppointmentList
                    appointments={filteredAppointments}
                    checkedItems={checkedItems}
                    onItemCheckChange={handleItemCheckChange}
                    allSelected={allSelected}
                    onToggleAll={handleToggleAll}
                  />
                ) : (
                  <NoAppointment />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Appointments;
